import React, { useEffect, useMemo, useState, useRef } from "react";
import { Modal, Form, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getActivePermissionList, addRoleApi, updateRoleApi } from "store/actions";
import LoadingButton from "components/shared/LoadingButton";

const ACTION_TABS = [
  { key: "all", label: "All" },
  { key: "view", label: "View" },
  { key: "add", label: "Add" },
  { key: "edit", label: "Edit" },
  { key: "delete", label: "Delete" },
  { key: "exports", label: "Export" },
];

export default function AddUpdateRole({
  show,
  handleClose,
  handleConfirm,
  title,
  loading,
  rowData,
}) {
  const dispatch = useDispatch();
  const { permissionList } = useSelector(({ Permission }) => Permission);

  const isUpdateMode = !!rowData;

  const [roleName, setRoleName] = useState(rowData?.roleName || "");
  const [description, setDescription] = useState(rowData?.description || "");
  const [roleNameError, setRoleNameError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeAction, setActiveAction] = useState("all");
  const [selectedPermissions, setSelectedPermissions] = useState(
    rowData?.permissions || [],
  );
  const [permissionsError, setPermissionsError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setRoleName(rowData?.roleName || "");
    setDescription(rowData?.description || "");
    setSelectedPermissions(rowData?.permissions || []);
  }, [rowData]);

  useEffect(() => {
    if (show && !isUpdateMode) {
      dispatch(getActivePermissionList({}));
    }
  }, [dispatch, show, isUpdateMode]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const flatPermissions = useMemo(() => {
    return Array.isArray(permissionList)
      ? permissionList
      : permissionList?.data || [];
  }, [permissionList]);

  const permissionById = useMemo(() => {
    const map = {};
    flatPermissions.forEach((p) => {
      map[p._id] = p;
    });
    return map;
  }, [flatPermissions]);

  const filteredPermissions = useMemo(() => {
    let list = flatPermissions;

    if (activeAction !== "all") {
      list = list.filter(
        (p) => p.action?.toLowerCase() === activeAction,
      );
    }

    if (search.trim()) {
      const term = search.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.permissionKey?.toLowerCase().includes(term) ||
          p.module?.toLowerCase().includes(term) ||
          p.action?.toLowerCase().includes(term),
      );
    }

    return list;
  }, [flatPermissions, search, activeAction]);

  const togglePermission = (id) => {
    setSelectedPermissions((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
    setPermissionsError("");
  };

  const removePermission = (id) => {
    setSelectedPermissions((prev) => prev.filter((p) => p !== id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const trimmedName = roleName.trim();
    const trimmedDescription = description.trim();

    let hasError = false;
    setRoleNameError("");
    setDescriptionError("");
    setPermissionsError("");

    if (!trimmedName) {
      setRoleNameError("Role name is required");
      hasError = true;
    }
    if (!trimmedDescription) {
      setDescriptionError("Description is required");
      hasError = true;
    }
    if (!isUpdateMode && selectedPermissions.length === 0) {
      setPermissionsError("Select at least one permission");
      hasError = true;
    }

    if (hasError) return;

    setSubmitLoading(true);

    if (isUpdateMode) {
      const payload = {
        _id: rowData?._id,
        roleName: trimmedName,
        description: trimmedDescription,
        permissions: selectedPermissions,
      };

      dispatch(
        updateRoleApi({
          data: payload,
          callBack: (err, response) => {
            setSubmitLoading(false);
            if (err) return;
            handleConfirm(response);
          },
        }),
      );
    } else {
      const payload = {
        roleName: trimmedName,
        description: trimmedDescription,
        permissions: selectedPermissions,
      };

      dispatch(
        addRoleApi({
          data: payload,
          callBack: (err, response) => {
            setSubmitLoading(false);
            if (err) return;
            handleConfirm(response);
          },
        }),
      );
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header className="py-3 text-dark" closeButton>
        <Modal.Title className="fw-bold text-dark">{title}</Modal.Title>
      </Modal.Header>

      <Form noValidate onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Role Name (half) + Description (half) */}
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Role Name</Form.Label>
                <Form.Control
                  type="text"
                  name="roleName"
                  placeholder="Role name..."
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  isInvalid={!!roleNameError}
                />
                <Form.Control.Feedback type="invalid">
                  {roleNameError}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Description</Form.Label>
                <Form.Control
                  type="text"
                  name="description"
                  placeholder="Description..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  isInvalid={!!descriptionError}
                />
                <Form.Control.Feedback type="invalid">
                  {descriptionError}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* Permissions (full width) */}
          {!isUpdateMode && (
            <Form.Group
              className="mb-3"
              ref={wrapperRef}
              style={{ position: "relative" }}
            >
              <Form.Label className="fw-bold">Permissions</Form.Label>

              {selectedPermissions.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {selectedPermissions.map((id) => {
                    const perm = permissionById[id];
                    if (!perm) return null;
                    return (
                      <span
                        key={id}
                        className="badge bg-primary d-flex align-items-center"
                        style={{ fontSize: "0.85rem", padding: "6px 8px" }}
                      >
                        {perm.permissionKey}
                        <span
                          role="button"
                          className="ms-2"
                          style={{ cursor: "pointer" }}
                          onClick={() => removePermission(id)}
                        >
                          &times;
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}

              <div className="d-flex flex-wrap gap-2 mb-3 mt-1">
                {ACTION_TABS.map((tab) => {
                  const isActive = activeAction === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveAction(tab.key)}
                      className="btn btn-sm rounded-pill"
                      style={{
                        padding: "4px 16px",
                        border: "1px solid #6f42c1",
                        backgroundColor: isActive ? "#6f42c1" : "#fff",
                        color: isActive ? "#fff" : "#6f42c1",
                      }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              <Form.Control
                type="text"
                placeholder="Search and select permissions..."
                value={search}
                onFocus={() => setDropdownOpen(true)}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setDropdownOpen(true);
                }}
                isInvalid={!!permissionsError}
              />
              <Form.Control.Feedback type="invalid">
                {permissionsError}
              </Form.Control.Feedback>

              {dropdownOpen && (
                <div
                  className="border rounded bg-white shadow-sm mt-1"
                  style={{
                    position: "absolute",
                    zIndex: 1060,
                    width: "100%",
                    maxHeight: "220px",
                    overflowY: "auto",
                  }}
                >
                  {filteredPermissions.length === 0 && (
                    <div className="p-2 text-muted small">
                      No permissions found
                    </div>
                  )}
                  {filteredPermissions.map((perm) => (
                    <div
                      key={perm._id}
                      className="px-2 py-1 d-flex align-items-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => togglePermission(perm._id)}
                    >
                      <Form.Check
                        type="checkbox"
                        readOnly
                        checked={selectedPermissions.includes(perm._id)}
                        className="me-2"
                      />
                      <span>
                        <strong>{perm.permissionKey}</strong>{" "}
                        <span className="text-muted small">
                          ({perm.module} / {perm.action})
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>
          )}

          <div className="d-flex justify-content-end">
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={loading || submitLoading}
            >
              Submit
            </LoadingButton>
          </div>
        </Modal.Body>
      </Form>
    </Modal>
  );
}